import {
  FOLDER_ACCESS_MANAGER,
  FOLDER_TYPES,
  FoldersV2,
} from 'lib/api/folders/getFoldersV2';
import { ListDropdownMenu } from 'lib/components';
import { AccessRole } from 'lib/const';
import { useAuth } from 'lib/context';
import {
  checkIfFeatureIsEnabled,
  PackageNameLabel,
  productFeature,
} from 'lib/utils/productFeature';
import React from 'react';
import { BiDotsVerticalRounded } from 'react-icons/bi';
import { MdDeleteForever, MdEdit, MdLock } from 'react-icons/md';
import styled, { useTheme } from 'styled-components/macro';
import selectors from '../../../../../../cypress/selectors';
import { Data } from 'lib/components/ListDropdownMenu';
import { FOLDER_SHARED_AS } from './types';
import { Button } from 'react-covideo-common';
import { useTranslation } from 'react-i18next';
import { getSecondaryColor } from 'lib/context/theme/utils';
import { IoMdRemoveCircle } from 'react-icons/io';

const CustomListDropdownMenu = styled(ListDropdownMenu)`
  width: 270px;
  right: 0px;
  box-shadow:
    0px 0px 4px ${({ theme }) => getSecondaryColor(theme, 10)},
    0px 6px 12px ${({ theme }) => getSecondaryColor(theme, 5)};
  border-radius: 8px;
  padding: 8px 0px;
`;

const Wrapper = styled.div`
  position: relative;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.white[100]};

  .show-more-dropdown {
    right: 0;
  }
`;

const showShareOption = (params: {
  hasNewFoldersAccess: boolean;
  restrictFolderSharingToAdmins: string;
  access: string;
  accessManager?: FOLDER_ACCESS_MANAGER;
}) => {
  const {
    hasNewFoldersAccess,
    restrictFolderSharingToAdmins,
    access,
    accessManager,
  } = params;
  const isAdmin = access === AccessRole.ADMIN;
  const sharingAllowed = accessManager !== FOLDER_ACCESS_MANAGER.NONE;
  const showShareOptionForOldUsers =
    restrictFolderSharingToAdmins?.toString() === '1'
      ? isAdmin
      : sharingAllowed;
  const showShareOptionForNewUsers = isAdmin && sharingAllowed;
  return hasNewFoldersAccess
    ? showShareOptionForNewUsers
    : showShareOptionForOldUsers;
};

type Props = {
  currentFolder: FoldersV2;
  openEditFolderModal: () => void;
  openDeleteFolderModal: () => void;
  handleOpenModal: () => void;
  handleStopSharingClick: () => void;
};
export const FolderActions = ({
  openEditFolderModal,
  openDeleteFolderModal,
  handleOpenModal,
  handleStopSharingClick,
  currentFolder,
}: Props) => {
  const { userData } = useAuth();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [showMoreDropdown, setShowMoreDropdown] = React.useState(false);

  const isFolderSharingEnabled = checkIfFeatureIsEnabled(
    userData,
    productFeature.FOLDER_SHARING
  );

  const showSharingOption = showShareOption({
    hasNewFoldersAccess: !!parseInt(
      userData.customer.hasNewFoldersAccess || '0',
      10
    ),
    restrictFolderSharingToAdmins:
      userData.reseller.restrictFolderSharingToAdmins,
    access: userData.access,
    accessManager: currentFolder.accessManager,
  });

  const showRenameOption = currentFolder.folderType !== FOLDER_TYPES.DEFAULT;
  const showDeleteOption = currentFolder.folderType !== FOLDER_TYPES.DEFAULT;
  const showStopSharingOption =
    currentFolder.folderType === FOLDER_TYPES.SHARED &&
    String(currentFolder?.userId) === userData.userId;
  const showSubtitle = showStopSharingOption && !!userData.isOrganizationAdmin;

  const moreDropdownItems = [
    showRenameOption && {
      title: t('Edit'),
      icon: (
        <MdEdit color={colors.neutral[60]} style={{ width: 20, height: 20 }} />
      ),
      onClick: () => openEditFolderModal(),
    },
    showSharingOption && {
      title: t('Share'),
      icon: (
        <MdLock color={colors.neutral[60]} style={{ width: 20, height: 20 }} />
      ),
      onClick: () => handleOpenModal(),
      productFeatureId: productFeature.FOLDER_SHARING,
      isDisabled: !isFolderSharingEnabled,
      productTooltipStyle: {
        right: '0px',
        left: '250px',
        top: '77px',
      },
      nextPlan: PackageNameLabel.TEAMS,
      subtitle: showSubtitle
        ? {
            [FOLDER_SHARED_AS.COMPANY_ADMIN]: t('Shared with Org'),
            [FOLDER_SHARED_AS.OEM_ADMIN]: t('Shared with OEM'),
          }[currentFolder?.folderSharedAs || ''] || ''
        : '',
    },
    showStopSharingOption && {
      title: t('Stop Sharing'),
      icon: (
        <IoMdRemoveCircle
          color={colors.danger[100]}
          style={{ width: 20, height: 20 }}
        />
      ),
      onClick: () => handleStopSharingClick(),
    },
    showDeleteOption && {
      title: t('Delete'),
      titleColor: colors.danger[100],
      icon: (
        <MdDeleteForever
          color={colors.danger[100]}
          style={{ width: 20, height: 20 }}
        />
      ),
      onClick: () => openDeleteFolderModal(),
    },
  ].filter(Boolean) as Data[];

  const isFolderOwner = currentFolder.userId?.toString() === userData.userId;
  const isAdmin =
    currentFolder.accessManager === FOLDER_ACCESS_MANAGER.ADMIN &&
    userData.access === AccessRole.ADMIN;

  const showThreeDotMenu =
    !!moreDropdownItems.length &&
    currentFolder &&
    Number(currentFolder?.folderId) > 0 &&
    (isFolderOwner || isAdmin);

  return showThreeDotMenu ? (
    <Wrapper
      data-cy={selectors.libraryPage.sharedFolderDropdown}
      onClick={() => setShowMoreDropdown(true)}
    >
      <Button
        variant='ghost'
        icon={<BiDotsVerticalRounded size={20} />}
        style={{ background: colors.white[100] }}
      />
      <CustomListDropdownMenu
        isMenuOpen={showMoreDropdown}
        items={moreDropdownItems}
        id={'show-more-dropdown-folder'}
        className='show-more-dropdown'
        setIsMenuOpen={() => setShowMoreDropdown(false)}
      />
    </Wrapper>
  ) : (
    <></>
  );
};
